import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils';
import { Button } from './Button';
import { Card, CardContent } from './Card';

export const STEPS_SCROLL_ATTR = 'data-steps-scroll';

const DEFAULT_SCROLL_ROOT = `[${STEPS_SCROLL_ATTR}]`;
const ACTIVE_OFFSET_PX = 48;

type StepsContextValue = {
  activeIndex: number;
  ids: readonly string[];
  register: (id: string) => void;
  unregister: (id: string) => void;
  registerEl: (id: string, el: HTMLElement | null) => void;
  activate: (index: number) => void;
  scrollRootSelector: string;
};

const StepsContext = createContext<StepsContextValue | null>(null);

const findScrollRoot = (el: HTMLElement | null, selector: string) => el?.closest(selector);

const findScrollRootFromMapped = (
  ids: readonly string[],
  stepEls: Map<string, HTMLElement>,
  selector: string,
) => {
  for (const id of ids) {
    const root = findScrollRoot(stepEls.get(id) ?? null, selector);
    if (root) return root;
  }
  return null;
};

const sameIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/** Visual order — `register` alone would follow mount order, not DOM order. */
const sortIdsByDocument = (ids: readonly string[], stepEls: Map<string, HTMLElement>) => {
  return [...ids].sort((left, right) => {
    const elA = stepEls.get(left);
    const elB = stepEls.get(right);
    if (!elA || !elB) return 0;
    const position = elA.compareDocumentPosition(elB);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
};

export interface StepsProps {
  children: ReactNode;
  /** CSS selector for the scroll container that drives the active step. */
  scrollRoot?: string;
  className?: string;
}

/**
 * Numbered step list. The filled icon follows the section at the top of `scrollRoot`.
 * Clicking a header scrolls that step to the top.
 */
export const Steps = ({ children, scrollRoot = DEFAULT_SCROLL_ROOT, className }: StepsProps) => {
  const [ids, setIds] = useState<string[]>([]);
  const stepEls = useRef(new Map<string, HTMLElement>());
  const [activeId, setActiveId] = useState<string | null>(null);
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const activeIndex = (() => {
    if (ids.length === 0) return 0;
    if (activeId == null) return 0;
    const index = ids.indexOf(activeId);
    return index === -1 ? 0 : index;
  })();

  const register = useCallback((id: string) => {
    setIds((prev) => {
      const next = prev.includes(id) ? prev : [...prev, id];
      const ordered = sortIdsByDocument(next, stepEls.current);
      return sameIds(prev, ordered) ? prev : ordered;
    });
  }, []);

  useLayoutEffect(() => {
    setIds((prev) => {
      const ordered = sortIdsByDocument(prev, stepEls.current);
      return sameIds(prev, ordered) ? prev : ordered;
    });
  }, [children]);

  const unregister = useCallback((id: string) => {
    setIds((prev) => {
      const removedIndex = prev.indexOf(id);
      const next = prev.filter((existing) => existing !== id);
      if (removedIndex === -1) return prev;
      setActiveId((current) => {
        if (current !== id) return current;
        if (next.length === 0) return null;
        return next[Math.min(removedIndex, next.length - 1)] ?? null;
      });
      return next;
    });
    stepEls.current.delete(id);
  }, []);

  const registerEl = useCallback((id: string, el: HTMLElement | null) => {
    if (el) stepEls.current.set(id, el);
    else stepEls.current.delete(id);
  }, []);

  const activate = useCallback((index: number) => {
    const nextId = idsRef.current[index];
    if (nextId) setActiveId((prev) => (prev === nextId ? prev : nextId));
  }, []);

  const syncActiveFromScroll = useCallback(() => {
    const entries: Array<[number, HTMLElement]> = [];
    ids.forEach((id, index) => {
      const el = stepEls.current.get(id);
      if (el) entries.push([index, el]);
    });
    if (!entries.length) return;

    const root = findScrollRootFromMapped(ids, stepEls.current, scrollRoot);
    if (!root) return;

    const atBottom =
      root.scrollTop > 8 && root.scrollTop + root.clientHeight >= root.scrollHeight - 2;
    if (atBottom) {
      activate(entries[entries.length - 1][0]);
      return;
    }

    const rootTop = root.getBoundingClientRect().top;
    let next = entries[0][0];
    for (const [index, el] of entries) {
      if (el.getBoundingClientRect().top - rootTop <= ACTIVE_OFFSET_PX) {
        next = index;
      }
    }
    activate(next);
  }, [activate, ids, scrollRoot]);

  useEffect(() => {
    const root = findScrollRootFromMapped(ids, stepEls.current, scrollRoot);
    if (!root) return undefined;

    let hideScrollbar: number;
    const onScroll = () => {
      syncActiveFromScroll();
      root.setAttribute('data-scrolling', '');
      window.clearTimeout(hideScrollbar);
      hideScrollbar = window.setTimeout(() => root.removeAttribute('data-scrolling'), 700);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    syncActiveFromScroll();
    return () => {
      root.removeEventListener('scroll', onScroll);
      window.clearTimeout(hideScrollbar);
    };
  }, [ids, scrollRoot, syncActiveFromScroll]);

  const value = useMemo(
    () => ({
      activeIndex,
      ids,
      register,
      unregister,
      registerEl,
      activate,
      scrollRootSelector: scrollRoot,
    }),
    [activeIndex, ids, register, unregister, registerEl, activate, scrollRoot],
  );

  return (
    <StepsContext.Provider value={value}>
      <div data-slot="steps" className={cn('tw', className)}>
        {children}
      </div>
    </StepsContext.Provider>
  );
};

export interface StepProps {
  title: ReactNode;
  description?: ReactNode;
  /** Replaces the step number in the icon button. */
  icon?: ReactNode;
  /** Shown in the icon button when `icon` is omitted. Defaults to the 1-based index. */
  stepNumber?: ReactNode;
  /** Active when this step is used without a `Steps` parent. */
  defaultActive?: boolean;
  /**
   * Stay mounted, but omit this step from the index.
   * Children keep their state when `hidden` toggles.
   */
  hidden?: boolean;
  className?: string;
  children: ReactNode;
}

export const Step = ({
  title,
  description,
  icon,
  stepNumber,
  defaultActive = false,
  hidden = false,
  className,
  children,
}: StepProps) => {
  const id = useId();
  const steps = useContext(StepsContext);
  const register = steps?.register;
  const unregister = steps?.unregister;
  const index = steps ? steps.ids.indexOf(id) : 0;
  const resolvedIndex = index === -1 ? 0 : index;
  const stepRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (hidden || !register || !unregister) return undefined;
    register(id);
    return () => unregister(id);
  }, [hidden, id, register, unregister]);

  const setStepRef = useCallback(
    (el: HTMLDivElement | null) => {
      stepRef.current = el;
      steps?.registerEl(id, el);
    },
    [id, steps],
  );

  const isActive = !hidden && (steps ? steps.activeIndex === resolvedIndex : defaultActive);
  const label = stepNumber ?? resolvedIndex + 1;

  const scrollToStep = () => {
    const el = stepRef.current;
    const root = findScrollRoot(el, steps?.scrollRootSelector ?? DEFAULT_SCROLL_ROOT);
    steps?.activate(resolvedIndex);
    if (!el || !root) return;
    const top =
      root.scrollTop +
      el.getBoundingClientRect().top -
      root.getBoundingClientRect().top -
      ACTIVE_OFFSET_PX;
    root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  return (
    <div
      ref={hidden ? undefined : setStepRef}
      hidden={hidden}
      data-slot="step"
      data-step-index={hidden ? undefined : resolvedIndex}
      data-active={isActive ? '' : undefined}
      className={cn('tw mb-4 w-full space-y-2', hidden && 'hidden', className)}
    >
      {hidden ? null : (
        <div
          data-slot="step-header"
          className="tw flex cursor-pointer items-center gap-4"
          onClick={scrollToStep}
        >
          <Button
            type="button"
            variant={isActive ? 'default' : 'outline'}
            size="icon"
            className={cn(
              'tw shrink-0',
              isActive
                ? 'text-interaction-primary-content-default'
                : // `dark:` is required: Button `outline` sets `dark:bg-transparent`.
                  'bg-background-surface text-interaction-neutral-content-default dark:bg-background-surface',
            )}
          >
            {icon ?? <span className="text-xs font-medium">{label}</span>}
          </Button>
          <div>
            <h2 className="tw text-foreground-primary heading-md">{title}</h2>
            {description ? (
              <p className="tw text-foreground-secondary body-md">{description}</p>
            ) : null}
          </div>
        </div>
      )}
      <Card variant="outline" className="tw">
        <CardContent className="tw flex flex-col gap-4">{children}</CardContent>
      </Card>
    </div>
  );
};
